import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Loader2, Mail, Phone, Key, CheckCircle, Lock } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const location = window.location.pathname;
  // Detect user type from URL path: /driver/forgot-password or /user/forgot-password
  const userType = location.includes('/driver/') ? 'driver' : 'user';
  
  const logoUrl = 'https://customer-assets.emergentagent.com/job_transport-app-29/artifacts/c2qih6ut_J%20%26%20S%20Logo%2001%20%20%282%29_1024x1024.jpg';
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  const [step, setStep] = useState(1); // 1: Enter email, 2: Enter code, 3: New password, 4: Success
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phoneHint, setPhoneHint] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) {
      toast({ title: "Error", description: "Please enter your email", variant: "destructive" });
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/password-reset/request`, {
        email: email,
        user_type: userType
      });
      
      setPhoneHint(response.data.phone_hint || '');
      setStep(2);
      toast({
        title: "Code Sent!",
        description: response.data.message || "Check your phone for the reset code",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to send reset code. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!code || code.length !== 6) {
      toast({ title: "Error", description: "Please enter the 6-digit code", variant: "destructive" });
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/password-reset/verify-code`, {
        email: email,
        code: code,
        user_type: userType
      });
      
      setStep(3);
      toast({
        title: "Code Verified!",
        description: "Now create your new password",
      });
    } catch (error) {
      toast({
        title: "Invalid Code",
        description: error.response?.data?.detail || "Please check the code and try again",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast({ title: "Error", description: "Password must be at least 6 characters", variant: "destructive" });
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast({ title: "Error", description: "Passwords do not match", variant: "destructive" });
      return;
    }
    
    setIsLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/password-reset/reset`, {
        email: email,
        code: code,
        new_password: newPassword,
        user_type: userType
      });
      
      setStep(4);
      toast({
        title: "Password Reset!",
        description: "You can now login with your new password",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to reset password. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const goToLogin = () => {
    if (userType === 'driver') {
      navigate('/driver/login');
    } else {
      navigate('/user/login');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-amber-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-6 sm:p-8 bg-white/95 backdrop-blur shadow-2xl">
        {/* Header */}
        <div className="text-center mb-6">
          <img src={logoUrl} alt="J-Style Services" className="h-16 w-16 mx-auto mb-4 object-contain" />
          <h1 className="text-2xl font-bold text-slate-800">
            {userType === 'driver' ? 'Driver ' : ''}Password Reset
          </h1>
          <p className="text-gray-600 mt-1">
            {step === 1 && "Enter your email to receive a reset code"}
            {step === 2 && `Enter the 6-digit code sent to ${phoneHint}`}
            {step === 3 && "Create your new password"}
            {step === 4 && "Your password has been reset!"}
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                ${step >= s ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                {step > s ? <CheckCircle size={18} /> : s}
              </div>
              {s < 4 && <div className={`w-8 h-1 ${step > s ? 'bg-amber-500' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Enter Email */}
        {step === 1 && (
          <form onSubmit={handleRequestReset} className="space-y-4">
            <div>
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail size={16} /> Email Address
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1"
                required
                data-testid="reset-email-input"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-amber-500 hover:bg-amber-600"
              disabled={isLoading}
              data-testid="send-code-btn"
            >
              {isLoading ? <Loader2 className="animate-spin mr-2" size={18} /> : <Mail size={18} className="mr-2" />}
              Send Reset Code
            </Button>
          </form>
        )}

        {/* Step 2: Enter Code */}
        {step === 2 && (
          <form onSubmit={handleVerifyCode} className="space-y-4">
            <div>
              <Label htmlFor="code" className="flex items-center gap-2">
                <Key size={16} /> 6-Digit Code
              </Label>
              <Input
                id="code"
                type="text"
                placeholder="Enter 6-digit code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="mt-1 text-center text-2xl tracking-widest font-mono"
                maxLength={6}
                required
                data-testid="reset-code-input"
              />
              <p className="text-xs text-gray-500 mt-1">Code expires in 15 minutes</p>
            </div>
            <Button 
              type="submit" 
              className="w-full bg-amber-500 hover:bg-amber-600"
              disabled={isLoading || code.length !== 6}
              data-testid="verify-code-btn"
            >
              {isLoading ? <Loader2 className="animate-spin mr-2" size={18} /> : <CheckCircle size={18} className="mr-2" />}
              Verify Code
            </Button>
            <Button 
              type="button" 
              variant="ghost"
              className="w-full"
              onClick={() => setStep(1)}
            >
              Didn't receive code? Try again
            </Button>
          </form>
        )}

        {/* Step 3: New Password */}
        {step === 3 && (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <Label htmlFor="newPassword" className="flex items-center gap-2">
                <Lock size={16} /> New Password
              </Label>
              <Input
                id="newPassword"
                type="password"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mt-1"
                minLength={6}
                required
                data-testid="new-password-input"
              />
            </div>
            <div>
              <Label htmlFor="confirmPassword" className="flex items-center gap-2">
                <Lock size={16} /> Confirm Password
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1"
                minLength={6}
                required
                data-testid="confirm-password-input"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-green-600 hover:bg-green-700"
              disabled={isLoading}
              data-testid="reset-password-btn"
            >
              {isLoading ? <Loader2 className="animate-spin mr-2" size={18} /> : <CheckCircle size={18} className="mr-2" />}
              Reset Password
            </Button>
          </form>
        )}

        {/* Step 4: Success */}
        {step === 4 && (
          <div className="text-center space-y-4">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle size={40} className="text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-green-700">Password Reset Successful!</h2>
            <p className="text-gray-600">You can now login with your new password.</p>
            <Button 
              className="w-full bg-amber-500 hover:bg-amber-600"
              onClick={goToLogin}
              data-testid="go-to-login-btn"
            >
              Go to Login
            </Button>
          </div>
        )}

        {/* Back to Login Link */}
        {step !== 4 && (
          <div className="mt-6 text-center">
            <button
              onClick={goToLogin}
              className="text-amber-600 hover:text-amber-700 text-sm flex items-center justify-center gap-1 mx-auto"
            >
              <ArrowLeft size={16} />
              Back to Login
            </button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ForgotPassword;
